// Confirmation step before an irreversible act — a native <dialog> with
// role="alertdialog", opened with showModal() so it stacks in the top
// layer above any other open modal (the photo lightbox, ADR-0169) and
// makes everything under it inert.
//
// Deliberate differences from Lightbox: no scrim-click close (an
// accidental click must never decide), the initial focus is the cancel
// button (the safe choice), and Escape cancels THIS dialog only — it is
// rendered as a React sibling of whatever opened it, never inside it, so
// neither the native `cancel` nor the keydown reaches an underlying
// dialog.
//
// The Admin screens' inline alertdialogs are not migrated to this yet
// (separate follow-up).

import { useEffect, useId, useRef, type ReactNode } from "react";

export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  /** Body copy, referenced by aria-describedby. */
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
};

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancelar",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // showModal() would focus the first focusable child on its own;
      // pin it to the cancel button whatever the DOM order becomes.
      cancelRef.current?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="cdlg"
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={descId}
      // Escape fires `cancel` on the top-most modal: keep the decision in
      // React state instead of letting the UA close it behind our back.
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }}
    >
      {open && (
        <div className="modal">
          <div className="modal__head">
            <h2 className="modal__t" id={titleId}>
              {title}
            </h2>
          </div>
          <div className="modal__body">
            <p className="cdlg__desc" id={descId}>
              {description}
            </p>
          </div>
          <div className="modal__foot">
            <button ref={cancelRef} type="button" className="btn btn--secondary" onClick={onCancel}>
              {cancelLabel}
            </button>
            <button type="button" className="btn btn--danger-solid" onClick={onConfirm}>
              {confirmLabel}
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
}
